import LogRocket from "logrocket";
import moment from "moment";
import { get } from "lodash";
import { call, cancelled, delay, put, race, select, take, takeLatest } from "redux-saga/effects";
import { eventChannel } from "redux-saga";
import { endFlow, startFlow } from "student-front-commons/src/actions/flow";
import { getFlow, getFlowEnd, getFlowStart } from "student-front-commons/src/selectors/flow";
import {
  CLOSE_MASTERY_TEST_EXECUTION_FLOW,
  CLOSE_UNIT_EXECUTION_FLOW,
  PLAY_ITEM_VIDEO_FLOW,
  REQUIRE_USER_PLAY_FLOW,
} from "consts";
import { addBreadcrumb, logError } from "utils";
import {
  disableItemExecution,
  enableItemExecution,
  finishPlayItem,
  startPlayItem,
  updateItemMediaLoad,
  updateItemMediaProgress,
} from "student-front-commons/src/actions/itemExecution";
import { getCurrentItemExecutionProp } from "student-front-commons/src/selectors/itemExecution";
import BunnyStreamPlayer, { BUNNY_VIDEO_IFRAME_ID } from "core/video/BunnyStreamPlayer";

function playerEvents(player, listenTo) {
  return eventChannel((emitter) => {
    listenTo.map((event) => {
      LogRocket.log(`add event handler ${event}`);
      return player.on(event, (data) => {
        LogRocket.log({ event, data });
        emitter({ event, data });
      });
    });
    return () => {
      LogRocket.log("remove event handlers");
      listenTo.map((event) => player.off(event));
    };
  });
}

let player = null;

function getVideoShortTiming(item) {
  const fixedStart = item.videoStartTime
    .match(/.{2}/g)
    .reduce((acc, value, index) => acc.concat(index ? ":" : "").concat(value), "");
  const fixedEnd = item.videoEndTime
    .match(/.{2}/g)
    .reduce((acc, value, index) => acc.concat(index ? ":" : "").concat(value), "");

  return {
    startSeconds: moment.duration(fixedStart).asSeconds(),
    endSeconds: moment.duration(fixedEnd).asSeconds(),
  };
}

function* waitForVideoIframe() {
  // this is required, because we need the dom ready to start the player
  while (!document.getElementById(BUNNY_VIDEO_IFRAME_ID)) {
    yield delay(100);
  }
}

function* loadVideoPlayer(item) {
  yield call(waitForVideoIframe);

  if (!player || !player.isCurrentVideo(item.videoLink)) {
    if (player) {
      yield player.destroy();
    }

    player = new BunnyStreamPlayer(BUNNY_VIDEO_IFRAME_ID, {
      url: item.videoLink,
      controls: false,
      loop: false,
      autoplay: false,
      muted: false,
      preload: true,
      responsive: false,
      rememberPosition: false,
      playsinline: true,
      chromecast: false,
      disableAirplay: true,
      disableIosPlayer: true,
      showHeatmap: false,
      showSpeed: false,
      compactControls: true,
    });
    yield player.ready();
    addBreadcrumb({
      category: "flow",
      message: `Video player loaded for ${item.videoLink}`,
    });
  }

  return player;
}

function* prepareVideoShortPlayer(player, item) {
  // await video load, because we can not set cuepoints before video has a defined duration
  while (!(yield player.getDuration())) {
    yield delay(50);
  }

  const { startSeconds, endSeconds } = getVideoShortTiming(item);
  const totalDuration = yield player.getDuration();

  player.clearCuePoints();
  yield player.addCuePoint(Math.min(endSeconds, totalDuration - 0.5));
  yield player.setCurrentTime(startSeconds);

  let currentTime = null;
  do {
    currentTime = yield player.getCurrentTime();
    yield delay(50);
  } while (currentTime === null || currentTime < startSeconds - 0.1 || currentTime > startSeconds + 0.1);
}

function* preparePlayer(item) {
  const videoPlayer = yield call(loadVideoPlayer, item);

  if (item.type.key === "VIDEO_SHORT") {
    yield call(prepareVideoShortPlayer, videoPlayer, item);
  }

  yield videoPlayer.pause();

  return videoPlayer;
}

export function* prepareItemVideo(item) {
  try {
    yield call(preparePlayer, item);
  } catch (error) {
    logError({ error, flow: PLAY_ITEM_VIDEO_FLOW });
  } finally {
    if (yield cancelled()) {
      if (player) {
        yield player.destroy();
        player = null;
      }
    }
  }
}

function waitForVideoPlayback(player) {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      player.off("play", handleStart);
      player.off("timeupdate", handleStart);
      player.off("error", handleError);
    };

    const handleStart = () => {
      cleanup();
      resolve();
    };

    const handleError = (error) => {
      cleanup();
      reject(error);
    };

    player.on("play", handleStart);
    player.on("timeupdate", handleStart);
    player.on("error", handleError);
    player.play().catch(handleError);
  });
}

function* requireUserPlay(player) {
  yield put(startFlow(REQUIRE_USER_PLAY_FLOW));
  yield take(getFlowEnd(REQUIRE_USER_PLAY_FLOW));
  if (player) {
    LogRocket.log("show play modal after error");
    yield player.play();
    yield player.setMuted(false);
    yield player.setVolume(1);
  }
}

function* playVideo(player) {
  const playResult = yield race({
    started: call(waitForVideoPlayback, player),
    timeout: delay(1000),
  });

  if (playResult.timeout) {
    throw new Error("Video play timeout");
  }

  yield player.setMuted(false);
  yield player.setVolume(1);
}

export default function* () {
  yield takeLatest(getFlowStart(PLAY_ITEM_VIDEO_FLOW), function* () {
    yield race({
      closeUnit: take(getFlowStart(CLOSE_UNIT_EXECUTION_FLOW)),
      closeMasteryTest: take(getFlowStart(CLOSE_MASTERY_TEST_EXECUTION_FLOW)),
      call: call(function* () {
        const flow = yield select(getFlow(PLAY_ITEM_VIDEO_FLOW));
        const isInitialPlay = get(flow.params, "initialPlay", false);
        let channel = null;

        try {
          yield put(disableItemExecution());

          const item = yield select(getCurrentItemExecutionProp("item"));
          yield call(preparePlayer, item);

          const events = ["cuepoint", "bufferend", "ended", "timeupdate", "progress", "play", "bufferstart", "error"];
          channel = yield call(playerEvents, player, events);

          try {
            yield call(playVideo, player);
          } catch (e) {
            LogRocket.log(e);
            yield call(requireUserPlay, player);
          }

          yield put(startPlayItem(item.id, { isInitialPlay }));

          while (true) {
            //added race with delay because browsers start blocking the play without emitting error
            const raceWinner = yield race({
              timeout: delay(1000),
              events: take(channel),
            });

            if (raceWinner.timeout) {
              yield call(requireUserPlay, player);
              continue;
            }

            const { event, data } = raceWinner.events;

            addBreadcrumb({
              category: "flow",
              message: `Video ${event} for ${item.videoLink}`,
              data: {
                event,
                link: item.videoLink,
              },
            });

            if (event === "cuepoint") {
              yield put(finishPlayItem(item.id, { isInitialPlay }));
              yield player.pause();
              channel.close();
              return;
            }
            if (event === "error") {
              addBreadcrumb({
                category: "flow",
                message: `Video error ${data.name} on method ${data.method} with message: ${data.message}`,
              });
              if ((data.name === "NotAllowedError" || data.name === "AbortError") && data.method === "play") {
                yield call(requireUserPlay, player);
              }
            }
            if (event === "timeupdate" && item.type.key !== "VIDEO_SHORT") {
              yield put(updateItemMediaProgress(item.id, { duration: data.duration, currentTime: data.seconds }));
            }
            if (event === "progress" && item.type.key !== "VIDEO_SHORT") {
              yield put(updateItemMediaLoad(item.id, { percent: data.percent * 100 }));
            }
            if (event === "ended") {
              yield put(finishPlayItem(item.id, { isInitialPlay }));
              channel.close();
              return;
            }
          }
        } catch (error) {
          logError({ error, flow: PLAY_ITEM_VIDEO_FLOW });
        } finally {
          if (channel) {
            channel.close();
          }
          if (yield cancelled()) {
            if (player) {
              yield player.destroy();
              addBreadcrumb({
                category: "flow",
                message: "Player destroyed",
              });
              player = null;
            }
          } else {
            yield put(enableItemExecution());
          }
          yield put(endFlow(PLAY_ITEM_VIDEO_FLOW));
        }
      }),
    });
  });
}
